/**
 * Persistence of the manual video list view mode (M4 F13): 'cards' (today's grid) or 'table'
 * (VideoRunsTable with expandable run rows). Stored in localStorage under `listViewMode` so the
 * choice survives reloads without a server round trip; the server-side UI preference keeps
 * receiving the same value for other clients.
 */

export const LIST_VIEW_MODE_STORAGE_KEY = 'listViewMode';
export const LIST_VIEW_MODES = Object.freeze(['cards', 'table']);
export const DEFAULT_LIST_VIEW_MODE = 'cards';

function defaultStorage() {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (error) {
    return null;
  }
}

/** 'cards' | 'table'; anything else falls back to `fallback`. */
export function normalizeListViewMode(value, fallback = DEFAULT_LIST_VIEW_MODE) {
  const mode = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return LIST_VIEW_MODES.includes(mode) ? mode : fallback;
}

/** Reads the persisted mode (default 'cards'); never throws (private mode, quota, no window). */
export function readListViewMode(storage = defaultStorage()) {
  if (!storage) return DEFAULT_LIST_VIEW_MODE;
  try {
    return normalizeListViewMode(storage.getItem(LIST_VIEW_MODE_STORAGE_KEY));
  } catch (error) {
    return DEFAULT_LIST_VIEW_MODE;
  }
}

/** Persists the mode and returns the normalised value that was stored. */
export function writeListViewMode(mode, storage = defaultStorage()) {
  const normalized = normalizeListViewMode(mode);
  if (storage) {
    try {
      storage.setItem(LIST_VIEW_MODE_STORAGE_KEY, normalized);
    } catch (error) {
      // Storage unavailable: the in-memory choice still applies for this session.
    }
  }
  return normalized;
}
